import { useState, type ReactNode } from 'react';
import { PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { cn } from '../lib/cn';
import { AppNav, type AppNavItem } from './AppNav';
import { IconButton } from './IconButton';

export type AppShellNavItem = AppNavItem;

export type AppShellProps = {
  /** Primary nav destinations (AppNav). */
  nav: AppShellNavItem[];
  children: ReactNode;
  /**
   * Brand mark in the rail (logo). Falls back to title text when omitted.
   */
  brand?: ReactNode;
  /** Optional product title under brand (gallery). */
  title?: string;
  /**
   * Full-width strip above the rail and content (deploy notice, etc.).
   * Takes space from the shell so the rail still fills the remaining height.
   */
  banner?: ReactNode;
  /**
   * Content-column top bar (search, profile). Product Layout uses this.
   * Gallery may still put chrome in `headerEnd` (rail footer).
   */
  topBar?: ReactNode;
  /**
   * Optional content footer. Prefer `SupportStrip` for portal servicer contact
   * (phone / email / Contact us).
   */
  footer?: ReactNode;
  /**
   * @deprecated Prefer `topBar` for product chrome. Still rendered in the rail
   * footer for design-system gallery compatibility.
   */
  headerEnd?: ReactNode;
  /** Hide the primary rail entirely. */
  hideNav?: boolean;
  /**
   * Desktop icons-only rail. Uncontrolled when omitted (starts expanded unless
   * `defaultCollapsed`).
   */
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Mobile drawer open. Controlled when `mobileOpen` is provided. */
  mobileOpen?: boolean;
  defaultMobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
  /** Label above nav when the rail shows labels. */
  navLabel?: string;
  className?: string;
};

/**
 * Product chrome: primary rail (AppNav) + optional top bar + content column.
 *
 * Desktop: rail is in document flow (no content padding hacks).
 * Mobile: rail is an overlay drawer; open via `mobileOpen` / hamburger in topBar.
 */
export function AppShell({
  nav,
  children,
  brand,
  title = 'Pixel Servicing',
  banner,
  topBar,
  footer,
  headerEnd,
  hideNav = false,
  collapsed: collapsedProp,
  defaultCollapsed = false,
  onCollapsedChange,
  mobileOpen: mobileOpenProp,
  defaultMobileOpen = false,
  onMobileOpenChange,
  navLabel,
  className,
}: AppShellProps) {
  const [collapsedUncontrolled, setCollapsedUncontrolled] =
    useState(defaultCollapsed);
  const [mobileOpenUncontrolled, setMobileOpenUncontrolled] =
    useState(defaultMobileOpen);

  const collapsed = collapsedProp ?? collapsedUncontrolled;
  const mobileOpen = mobileOpenProp ?? mobileOpenUncontrolled;

  /** Product Layout passes open state; gallery keeps a permanent rail. */
  const drawerMode =
    mobileOpenProp !== undefined || onMobileOpenChange !== undefined;

  const setCollapsed = (next: boolean) => {
    if (collapsedProp === undefined) setCollapsedUncontrolled(next);
    onCollapsedChange?.(next);
  };

  const setMobileOpen = (next: boolean) => {
    if (mobileOpenProp === undefined) setMobileOpenUncontrolled(next);
    onMobileOpenChange?.(next);
  };

  return (
    <div
      className={cn(
        'flex h-dvh flex-col overflow-hidden bg-ds-app text-ds-text',
        className,
      )}
    >
      {banner}
      <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
        {!hideNav ? (
          <>
          {/* Mobile drawer backdrop (product only) */}
          {drawerMode ? (
            <div
              className={cn(
                'fixed inset-0 z-40 bg-ds-text/30 transition-opacity duration-ds-normal ease-ds lg:hidden',
                mobileOpen
                  ? 'opacity-100'
                  : 'pointer-events-none opacity-0',
              )}
              aria-hidden={!mobileOpen}
              onClick={() => setMobileOpen(false)}
            />
          ) : null}

          <aside
            id="app-shell-nav"
            className={cn(
              'flex h-full shrink-0 flex-col border-r border-ds-border bg-ds-surface',
              'transition-[width,transform] duration-ds-normal ease-ds',
              drawerMode
                ? cn(
                    'fixed inset-y-0 left-0 z-50 w-64',
                    mobileOpen ? 'translate-x-0' : '-translate-x-full',
                    'lg:static lg:z-auto lg:translate-x-0',
                    collapsed ? 'lg:w-14' : 'lg:w-48',
                  )
                : cn('static w-52', collapsed && 'w-14'),
            )}
            onClick={(e) => {
              // Close mobile drawer after navigating.
              if (
                drawerMode &&
                e.target instanceof Element &&
                e.target.closest('a')
              ) {
                setMobileOpen(false);
              }
            }}
          >
            <div
              className={cn(
                // Center brand in the rail column; mobile close is overlaid so it
                // does not pull the logo off-center.
                'relative flex items-center justify-center border-b border-ds-border px-2 py-2.5',
                collapsed ? 'lg:px-1' : null,
              )}
            >
              <div className="flex min-w-0 max-w-full items-center justify-center">
                {brand ? (
                  brand
                ) : (
                  <div className={cn('text-center', collapsed && 'lg:hidden')}>
                    <div className="text-ds-xs font-medium text-ds-muted">
                      Pixel Mortgage
                    </div>
                    <div className="truncate text-ds-md font-semibold text-ds-text">
                      {title}
                    </div>
                  </div>
                )}
              </div>

              {drawerMode ? (
                <IconButton
                  label="Close navigation"
                  className="absolute top-1/2 right-2 -translate-y-1/2 lg:hidden"
                  onClick={() => setMobileOpen(false)}
                >
                  <X className="size-4" />
                </IconButton>
              ) : null}
            </div>

            {navLabel && !collapsed ? (
              <div className="px-3 pt-2 text-ds-xs font-semibold tracking-wide text-ds-muted uppercase">
                {navLabel}
              </div>
            ) : null}

            <AppNav
              items={nav}
              collapsed={collapsed}
              className="min-h-0 flex-1 overflow-y-auto"
            />

            <div className="mt-auto space-y-2 border-t border-ds-border p-2">
              {headerEnd ? (
                <div className={cn(collapsed && 'lg:hidden')}>{headerEnd}</div>
              ) : null}

              <div className="hidden justify-end lg:flex">
                <IconButton
                  label={
                    collapsed ? 'Expand navigation' : 'Collapse navigation'
                  }
                  onClick={() => setCollapsed(!collapsed)}
                >
                  {collapsed ? (
                    <PanelLeftOpen className="size-4" />
                  ) : (
                    <PanelLeftClose className="size-4" />
                  )}
                </IconButton>
              </div>
            </div>
          </aside>
          </>
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {topBar ? (
            // Above scroll content + EntityHeader (z-30) so profile/search menus paint on top.
            <div className="relative z-50 shrink-0">{topBar}</div>
          ) : null}
          {/* The page's own content: the landmark screen readers jump to. */}
          <main className="relative z-0 min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
            {children}
          </main>
          {footer ? <div className="shrink-0">{footer}</div> : null}
        </div>
      </div>
    </div>
  );
}
